import { SignupMethodStep } from "./signup/SignupMethodStep";
import { CountryStep } from "./signup/CountryStep";
import { DetailsStep } from "./signup/DetailsStep";
import { VerifyStep } from "./signup/VerifyStep";
import { useSignUpFlow } from "../hooks/useSignUpFlow";
import { SuccessSheet } from "./signup/SuccessSheet";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { StepProgress } from "./signup/StepProgress";
import { useAuthMutations } from "../hooks/useAuthMutations";

export function SignupFlow() {
  const totalSteps = 4;
  const navigate = useNavigate();

  const { step, nextStep, prevStep, formData, updateData } = useSignUpFlow();

  const [showSuccess, setShowSuccess] = useState(false);

  // Registration returns a pending challenge. Tokens and AuthContext are
  // populated only after the backend accepts the email OTP in step 4.
  const {
    signupAsync,
    isSigningUp,
    signupError,
    verifyEmailAsync,
    isVerifyingEmail,
    verifyEmailError,
    resendEmailOtpAsync,
    isResendingEmailOtp,
    resendEmailOtpError,
  } = useAuthMutations();

  // SIGNUP HANDLER (CORE)
  const handleSignup = async (details) => {
    try {
      const firstName = details.firstName.trim();
      const lastName = details.lastName.trim();
      const fullName = `${firstName} ${lastName}`;

      // country, currency, phone and dialCode all come from CountryStep
      // (step 2) now, not guessed and not hardcoded.
      const challenge = await signupAsync({
        phone_number: formData.phone,
        whatsapp_id: `wa_${formData.phone.replace("+", "")}`,
        email: details.email,
        full_name: fullName,
        country_code: formData.country,
        default_currency: formData.currency,
        gender: details.gender,
        source_of_funds: details.sourceOfFunds,
        password: details.password,
        pin: details.pin,
      });

      updateData({
        firstName,
        lastName,
        name: fullName,
        email: details.email,
        verificationId: challenge.verification_id,
        emailHint: challenge.email_hint,
        resendAvailableAt: challenge.resend_available_at,
      });
      sessionStorage.setItem(
        "blupay_email_verification",
        JSON.stringify({
          email: details.email,
          verificationId: challenge.verification_id,
          emailHint: challenge.email_hint,
          resendAvailableAt: challenge.resend_available_at,
        }),
      );
      nextStep();
    } catch (err) {
      // signupError from useAuthMutations is rendered by the active step.
    }
  };

  const handleVerify = async (code) => {
    await verifyEmailAsync({
      verification_id: formData.verificationId,
      code,
    });
    sessionStorage.removeItem("blupay_email_verification");
    handleComplete();
  };

  const handleResend = async () => {
    const challenge = await resendEmailOtpAsync(formData.verificationId);
    updateData({
      emailHint: challenge.email_hint,
      resendAvailableAt: challenge.resend_available_at,
    });
    sessionStorage.setItem(
      "blupay_email_verification",
      JSON.stringify({
        email: formData.email,
        verificationId: formData.verificationId,
        emailHint: challenge.email_hint,
        resendAvailableAt: challenge.resend_available_at,
      }),
    );
    return challenge;
  };

  const handleComplete = () => {
    setShowSuccess(true);

    setTimeout(() => {
      setShowSuccess(false);
      // /auth/verify-email stored tokens and populated AuthContext.
      navigate("/home");
    }, 1500);
  };

  return (
    <div className="min-h-screen w-[100%] flex items-center justify-center bg-gray-50 p-0 sm:p-4">
      <div className="w-full max-w-[450px] bg-white sm:bg-transparent min-h-screen sm:h-full shadow-xl sm:shadow-none relative overflow-auto scrollbar-hidden">
        <div
          key={step}
          className="flex-grow animate-in fade-in slide-in-from-right-8 transition duration-500 ease-out"
        >
          {/* Step 1: signup method — onNext() takes no arguments here; the
              "Continue with Email" path is the only one that reaches step 2,
              since the OAuth buttons redirect the browser away entirely */}
          {step === 1 && (
            <SignupMethodStep onBack={prevStep} onNext={() => nextStep()} />
          )}

          {/* Step 2: country + phone (drives currency, dial code) */}
          {step === 2 && (
            <CountryStep
              onBack={prevStep}
              defaultValues={formData}
              onNext={(countryData) => {
                updateData(countryData);
                nextStep();
              }}
            />
          )}

          {/* Step 3: name / email / password — connected to /auth/register */}
          {step === 3 && (
            <DetailsStep
              onBack={prevStep}
              onNext={handleSignup}
              loading={isSigningUp}
              error={
                signupError
                  ? typeof signupError === "string"
                    ? signupError
                    : signupError.message
                  : ""
              }
            />
          )}

          {/* Step 4: verify */}
          {step === 4 && (
            <VerifyStep
              email={formData.emailHint || formData.email}
              verificationId={formData.verificationId}
              resendAvailableAt={formData.resendAvailableAt}
              onBack={prevStep}
              onVerify={handleVerify}
              onResend={handleResend}
              loading={isVerifyingEmail}
              resending={isResendingEmailOtp}
              error={verifyEmailError || resendEmailOtpError}
            />
          )}

          <StepProgress step={step} totalSteps={totalSteps} />

          {showSuccess && <SuccessSheet />}
        </div>
      </div>
    </div>
  );
}
